// src/components/customer/CustomerSampleOrders.js

import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Package,
  Truck,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronRight,
  MessageSquare,
  Star,
  Send,
} from "lucide-react";
import { getAuth } from "firebase/auth";
import { getSampleOrderStatusLabel } from "../../utils/sampleOrderStatusLabels";

/**
 * Canonical sample order identity fields (companies/{companyId}/sampleOrders/{orderId}):
 * - createdBy (uid string)
 * - createdByName (string)
 * - createdByRole (lowercase string: "customer" | "owner" | "admin" | "enduser")
 *
 * ✅ This component uses ONLY canonical fields (no requestedBy / requester / legacy fallbacks).
 */

// ------------------------------
// Helpers
// ------------------------------
const getNormalizedAccountIds = (order) => {
  const ids = [
    order?.accountId,
    order?.locationId,
    ...(Array.isArray(order?.accountIds) ? order.accountIds : []),
  ];
  return Array.from(new Set(ids.map((id) => String(id || "").trim()).filter(Boolean)));
};

const isCustomerVisibleOrder = (order, uid, visibleAccountIds = []) => {
  if (!order) return false;

  const createdBy = (order.createdBy || "").toString();
  const role = (order.createdByRole || "").toString().trim().toLowerCase();
  const isDirectCustomerOrder = createdBy === String(uid) && role === "customer";

  if (isDirectCustomerOrder) return true;

  const allowedAccounts = new Set(
    (Array.isArray(visibleAccountIds) ? visibleAccountIds : [])
      .map((id) => String(id || "").trim())
      .filter(Boolean)
  );

  if (!allowedAccounts.size) return false;

  return getNormalizedAccountIds(order).some((id) => allowedAccounts.has(id));
};

const getCreatedAtMillis = (r) => {
  const ts = r?.createdAt;
  if (!ts) return 0;
  if (ts?.seconds) return ts.seconds * 1000;
  if (typeof ts?.toDate === "function") return ts.toDate().getTime();
  if (ts instanceof Date) return ts.getTime();
  const parsed = new Date(ts).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const getReadableDate = (r) => {
  const ms = getCreatedAtMillis(r);
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const normalizeStatus = (s) =>
  (s || "pending")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const getRequestedDeliveryLabel = (order) => {
  const requestedDate = order?.requestedDate || order?.deliveryDate;
  if (typeof requestedDate === "string" && requestedDate.trim()) return requestedDate.trim();
  return "—";
};

const getActualDeliveryLabel = (order) => {
  const deliveredAt =
    order?.actualDeliveryDate ||
    order?.deliveredAt ||
    order?.previouslyDeliveredAt ||
    order?.actualDeliveredAt;
  if (!deliveredAt) return "—";

  try {
    const d =
      typeof deliveredAt?.toDate === "function"
        ? deliveredAt.toDate()
        : deliveredAt?.seconds
        ? new Date(deliveredAt.seconds * 1000)
        : new Date(deliveredAt);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString();
  } catch {
    return "—";
  }
};

const STATUS_META = {
  pending: {
    label: "Pending",
    Icon: Clock,
    pill: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
  },
  approved: {
    label: "Approved",
    Icon: CheckCircle2,
    pill: "bg-emerald-50 text-emerald-800 border-emerald-200",
    dot: "bg-emerald-500",
  },
  shipped: {
    label: "Shipped",
    Icon: Truck,
    pill: "bg-blue-50 text-blue-800 border-blue-200",
    dot: "bg-blue-500",
  },
  delivered: {
    label: "Delivered",
    Icon: CheckCircle2,
    pill: "bg-slate-50 text-slate-800 border-slate-200",
    dot: "bg-slate-500",
  },
  completed: {
    label: "Completed",
    Icon: CheckCircle2,
    pill: "bg-emerald-50 text-emerald-800 border-emerald-200",
    dot: "bg-emerald-500",
  },
  "follow up": {
    label: "Follow Up",
    Icon: MessageSquare,
    pill: "bg-violet-50 text-violet-800 border-violet-200",
    dot: "bg-violet-500",
  },
  followup: {
    label: "Follow Up",
    Icon: MessageSquare,
    pill: "bg-violet-50 text-violet-800 border-violet-200",
    dot: "bg-violet-500",
  },
  rejected: {
    label: "Rejected",
    Icon: XCircle,
    pill: "bg-red-50 text-red-800 border-red-200",
    dot: "bg-red-500",
  },
};

const getStatusMeta = (statusRaw, sampleStatusLabelSettings = {}) => {
  const s = normalizeStatus(statusRaw);
  const base = STATUS_META[s] || STATUS_META.pending;
  return {
    ...base,
    label: getSampleOrderStatusLabel(base.label, sampleStatusLabelSettings),
  };
};

const getItemSummary = (r) => {
  const items = Array.isArray(r?.items) ? r.items : [];
  const itemCount = items.filter((i) => i?.productId).length;

  if (itemCount > 0) {
    const first = items.find((i) => i?.productName) || items[0] || null;
    const firstName = first?.productName || r?.productName || "Sample items";
    if (itemCount === 1) return firstName;
    return `${firstName} + ${itemCount - 1} more`;
  }

  return r?.productName || "Sample items";
};

const getQtySummary = (r) => {
  if (typeof r?.quantity === "number" && r.quantity > 0) return r.quantity;

  const items = Array.isArray(r?.items) ? r.items : [];
  const sum = items.reduce((acc, it) => acc + (Number(it?.quantity) || 0), 0);
  return sum || 0;
};

const getLocationLabel = (r) =>
  (r?.accountName || r?.customerName || r?.businessName || "").toString().trim();

const canLeaveFeedback = (order) => {
  const status = normalizeStatus(order?.status);
  return (
    status === "delivered" ||
    status === "follow up" ||
    status === "followup" ||
    status === "completed"
  );
};

const hasFeedback = (order) =>
  Number(order?.feedbackRating) > 0 ||
  (typeof order?.feedbackComments === "string" && order.feedbackComments.trim()) ||
  (typeof order?.feedbackUseCase === "string" && order.feedbackUseCase.trim());

// ------------------------------
// Component
// ------------------------------
const CustomerSampleOrders = ({
  requests,
  visibleAccountIds = [],
  repUser, // optional (display only)
  onSelectRequest, // optional: (request) => void
  onSubmitFeedback, // optional async (request, payload) => void
  feedbackSavingId = "",
  sampleStatusLabelSettings = {},
}) => {
  const auth = getAuth();
  const uid = auth.currentUser?.uid || null;

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [openFeedbackId, setOpenFeedbackId] = useState("");
  const [feedbackForms, setFeedbackForms] = useState({});
  const [feedbackError, setFeedbackError] = useState("");
  const [expandedRequestId, setExpandedRequestId] = useState("");

  const myRequests = useMemo(() => {
    const list = Array.isArray(requests) ? requests : [];
    if (!uid) return [];

    return list
      .filter((r) => isCustomerVisibleOrder(r, uid, visibleAccountIds))
      .sort((a, b) => getCreatedAtMillis(b) - getCreatedAtMillis(a));
  }, [requests, uid, visibleAccountIds]);

  const filteredRequests = useMemo(() => {
    let filtered = myRequests;

    if (statusFilter !== "all") {
      filtered = filtered.filter((r) => normalizeStatus(r?.status) === statusFilter);
    }

    const term = (searchTerm || "").trim().toLowerCase();
    if (term) {
      filtered = filtered.filter((r) => {
        const searchable = [
          r?.id,
          r?.orderNumber,
          r?.status,
          getItemSummary(r),
          getLocationLabel(r),
          r?.kitName,
          r?.createdByName, // canonical
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchable.includes(term);
      });
    }

    return filtered;
  }, [myRequests, searchTerm, statusFilter]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const counts = useMemo(() => {
    const c = { all: myRequests.length };
    for (const r of myRequests) {
      const s = normalizeStatus(r?.status);
      c[s] = (c[s] || 0) + 1;
    }
    return c;
  }, [myRequests]);

  const getFeedbackDraft = (request) => {
    const existing = feedbackForms[request?.id];
    if (existing) return existing;
    return {
      rating: Number(request?.feedbackRating) || 0,
      comments: request?.feedbackComments || "",
      useCase: request?.feedbackUseCase || "",
      interestLevel: request?.feedbackInterestLevel || "warm",
      nextStep: request?.feedbackNextStep || "follow_up",
    };
  };

  const setFeedbackDraft = (requestId, patch) => {
    setFeedbackForms((prev) => ({
      ...prev,
      [requestId]: {
        ...(prev[requestId] || {}),
        ...patch,
      },
    }));
  };

  const handleFeedbackSubmit = async (request) => {
    if (typeof onSubmitFeedback !== "function") return;

    const draft = getFeedbackDraft(request);
    if (!draft.rating) {
      setFeedbackError("Please choose a rating before sending feedback.");
      return;
    }

    setFeedbackError("");

    try {
      await onSubmitFeedback(request, draft);
      setOpenFeedbackId("");
    } catch (err) {
      setFeedbackError(err?.message || "Unable to save feedback right now.");
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-600" />
              <h3 className="font-semibold text-slate-900 text-sm truncate">My Sample Orders</h3>
            </div>
            <p className="mt-0.5 text-[11px] text-slate-500">
              {filteredRequests.length} shown • {myRequests.length} total
              {repUser?.fullName || repUser?.email ? (
                <>
                  <span className="text-slate-300"> • </span>
                  <span className="text-slate-600">
                    Rep:{" "}
                    <span className="font-medium">{repUser?.fullName || repUser?.email}</span>
                  </span>
                </>
              ) : null}
            </p>
          </div>

          <div className="shrink-0 flex items-center gap-2">
            <span className="text-[10px] px-2 py-1 rounded-full border border-slate-200 bg-white text-slate-600">
              Updated live
            </span>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          {!isMobile ? (
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by status, item, location…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
            </div>
          ) : null}

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            aria-label="Filter by status"
          >
            <option value="all">All ({counts.all || 0})</option>
            <option value="pending">{getSampleOrderStatusLabel("Pending", sampleStatusLabelSettings)} ({counts.pending || 0})</option>
            <option value="approved">{getSampleOrderStatusLabel("Approved", sampleStatusLabelSettings)} ({counts.approved || 0})</option>
            <option value="shipped">{getSampleOrderStatusLabel("Shipped", sampleStatusLabelSettings)} ({counts.shipped || 0})</option>
            <option value="delivered">{getSampleOrderStatusLabel("Delivered", sampleStatusLabelSettings)} ({counts.delivered || 0})</option>
            <option value="follow up">{getSampleOrderStatusLabel("Follow Up", sampleStatusLabelSettings)} ({(counts["follow up"] || 0) + (counts.followup || 0)})</option>
            <option value="completed">{getSampleOrderStatusLabel("Completed", sampleStatusLabelSettings)} ({counts.completed || 0})</option>
            <option value="rejected">Rejected ({counts.rejected || 0})</option>
          </select>
        </div>
      </div>

      {/* List */}
      <div className="p-2 sm:p-3">
        {filteredRequests.length === 0 ? (
          <div className="p-6 text-center">
            <div className="mx-auto w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-slate-500" />
            </div>
            <p className="mt-3 text-sm font-semibold text-slate-900">No sample orders found</p>
            <p className="mt-1 text-xs text-slate-500">
              Try changing your search or status filter.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filteredRequests.map((r) => {
              const statusMeta = getStatusMeta(r?.status, sampleStatusLabelSettings);
              const StatusIcon = statusMeta.Icon;

              const title = getItemSummary(r);
              const qty = getQtySummary(r);
              const location = getLocationLabel(r);
              const created = getReadableDate(r);

              const requestId = r?.id || "";
              const orderNo = r?.orderNumber ? `#${r.orderNumber}` : null;

              const clickable = typeof onSelectRequest === "function";
              const showFeedback = canLeaveFeedback(r);
              const feedbackOpen = openFeedbackId === requestId;
              const feedbackSaved = hasFeedback(r);
              const draft = getFeedbackDraft(r);
              const isExpanded = expandedRequestId === requestId;
              const repComments = String(r?.notes || "").trim();

              return (
                <li key={requestId || `${getCreatedAtMillis(r)}-${Math.random()}`} className="py-2">
                  <div className="rounded-xl px-3 py-2 hover:bg-slate-50 transition-colors">
                    <button
                      type="button"
                      onClick={() => {
                        if (clickable) {
                          onSelectRequest(r);
                          return;
                        }
                        setExpandedRequestId((prev) => (prev === requestId ? "" : requestId));
                      }}
                      className="w-full cursor-pointer text-left"
                    >
                      <div className="flex items-start gap-3">
                        <div className="pt-0.5">
                          <div className="w-9 h-9 rounded-xl border border-slate-200 bg-white flex items-center justify-center">
                            <StatusIcon className="w-4 h-4 text-slate-700" />
                          </div>
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-sm font-semibold text-slate-900 truncate">
                                  {title}
                                </span>
                                {orderNo ? (
                                  <span className="text-[11px] text-slate-400 truncate">{orderNo}</span>
                                ) : null}
                              </div>

                              <div className="mt-0.5 text-[11px] text-slate-500 truncate">
                                {location ? (
                                  <span className="text-slate-600">{location}</span>
                                ) : (
                                  <span className="text-slate-400">No location</span>
                                )}
                                <span className="text-slate-300"> • </span>
                                <span>Placed {created}</span>
                                {qty ? (
                                  <>
                                    <span className="text-slate-300"> • </span>
                                    <span>
                                      Qty <span className="font-mono">{qty}</span>
                                    </span>
                                  </>
                                ) : null}
                              </div>
                            </div>

                            <div className="shrink-0 flex items-center gap-2">
                              <span
                                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] font-medium ${statusMeta.pill}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dot}`} />
                                {statusMeta.label}
                              </span>

                              <ChevronRight
                                className={`w-4 h-4 text-slate-400 transition-transform ${
                                  !clickable && isExpanded ? "rotate-90" : ""
                                }`}
                              />
                            </div>
                          </div>

                          {r?.deliveryDate ? (
                            <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-1.5">
                              <Truck className="w-3.5 h-3.5 text-slate-400" />
                              <span className="truncate">
                                Delivery:{" "}
                                <span className="text-slate-700">{r.deliveryDate}</span>
                              </span>
                            </div>
                          ) : null}

                          {feedbackSaved ? (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-800">
                                <Star className="w-3.5 h-3.5 fill-current" />
                                Feedback received
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setFeedbackError("");
                                  setOpenFeedbackId((prev) => (prev === requestId ? "" : requestId));
                                }}
                                className="text-[11px] font-medium text-slate-600 underline decoration-slate-300 underline-offset-4 hover:text-slate-900"
                              >
                                {feedbackOpen ? "Hide feedback" : "View feedback"}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </button>

                    {!clickable && isExpanded ? (
                      <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              Items / Qty
                            </div>
                            <div className="mt-1 text-sm text-slate-900">
                              {title}
                              {qty ? ` • Qty ${qty}` : ""}
                            </div>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              Requested delivery
                            </div>
                            <div className="mt-1 text-sm text-slate-900">
                              {getRequestedDeliveryLabel(r)}
                            </div>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              Actual delivery
                            </div>
                            <div className="mt-1 text-sm text-slate-900">
                              {getActualDeliveryLabel(r)}
                            </div>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 sm:col-span-2">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              Comments from your rep
                            </div>
                            <div className="mt-1 whitespace-pre-wrap text-sm text-slate-900">
                              {repComments || "No rep comments yet."}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {showFeedback && (!feedbackSaved || feedbackOpen) ? (
                      <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              {feedbackSaved ? "Your feedback" : "How did this sample perform?"}
                            </p>
                            <p className="text-[11px] text-slate-500">
                              Share taste, fit, and next-step insight so your rep knows what to do next.
                            </p>
                          </div>
                          {!feedbackSaved ? (
                            <button
                              type="button"
                              onClick={() => {
                                setFeedbackError("");
                                setOpenFeedbackId((prev) => (prev === requestId ? "" : requestId));
                              }}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                              {feedbackOpen ? "Hide" : "Leave feedback"}
                            </button>
                          ) : null}
                        </div>

                        {feedbackOpen ? (
                          feedbackSaved ? (
                            <div className="mt-3 grid gap-3 md:grid-cols-2">
                              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Overall rating
                                </div>
                                <div className="mt-1 text-sm font-semibold text-slate-900">
                                  {draft.rating ? `${draft.rating}/5` : "Not provided"}
                                </div>
                              </div>
                              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Guest / kitchen reaction
                                </div>
                                <div className="mt-1 text-sm text-slate-900">{draft.interestLevel || "—"}</div>
                              </div>
                              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Best next step
                                </div>
                                <div className="mt-1 text-sm text-slate-900">{draft.nextStep || "—"}</div>
                              </div>
                              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Use case
                                </div>
                                <div className="mt-1 text-sm text-slate-900">{draft.useCase || "—"}</div>
                              </div>
                              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 md:col-span-2">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Notes for your rep
                                </div>
                                <div className="mt-1 whitespace-pre-wrap text-sm text-slate-900">
                                  {draft.comments || "No notes provided."}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-3 space-y-3">
                              {feedbackError ? (
                                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                                  {feedbackError}
                                </div>
                              ) : null}

                              <div>
                                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Overall rating
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {[1, 2, 3, 4, 5].map((value) => (
                                    <button
                                      key={value}
                                      type="button"
                                      onClick={() => setFeedbackDraft(requestId, { rating: value })}
                                      className={`inline-flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold ${
                                        Number(draft.rating) === value
                                          ? "border-blue-600 bg-blue-600 text-white"
                                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                                      }`}
                                    >
                                      {value}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <div className="grid gap-3 md:grid-cols-2">
                                <label className="block">
                                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Guest / kitchen reaction
                                  </div>
                                  <select
                                    value={draft.interestLevel}
                                    onChange={(e) =>
                                      setFeedbackDraft(requestId, { interestLevel: e.target.value })
                                    }
                                    className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                                  >
                                    <option value="hot">Strong interest</option>
                                    <option value="warm">Interested with tweaks</option>
                                    <option value="cold">Not a fit right now</option>
                                  </select>
                                </label>

                                <label className="block">
                                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Best next step
                                  </div>
                                  <select
                                    value={draft.nextStep}
                                    onChange={(e) => setFeedbackDraft(requestId, { nextStep: e.target.value })}
                                    className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                                  >
                                    <option value="follow_up">Have my rep follow up</option>
                                    <option value="quote">Send pricing / order info</option>
                                    <option value="resample">Try a revised sample</option>
                                    <option value="no_action">No action needed</option>
                                  </select>
                                </label>
                              </div>

                              <label className="block">
                                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  How did you use it?
                                </div>
                                <input
                                  type="text"
                                  value={draft.useCase}
                                  onChange={(e) => setFeedbackDraft(requestId, { useCase: e.target.value })}
                                  placeholder="Example: Weekend brunch special, staff tasting, plated with pasta..."
                                  className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                                />
                              </label>

                              <label className="block">
                                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Notes for your rep
                                </div>
                                <textarea
                                  rows={4}
                                  value={draft.comments}
                                  onChange={(e) => setFeedbackDraft(requestId, { comments: e.target.value })}
                                  placeholder="Tell us about flavor, prep, yield, customer reaction, pricing sensitivity, or anything else that matters."
                                  className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500"
                                />
                              </label>

                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => handleFeedbackSubmit(r)}
                                  disabled={feedbackSavingId === requestId}
                                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                                >
                                  <Send className="w-4 h-4" />
                                  {feedbackSavingId === requestId ? "Saving..." : "Send feedback"}
                                </button>
                              </div>
                            </div>
                          )
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CustomerSampleOrders;
